import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { SessionService } from '@domain/application/observable-services/session.service';
import { DocumentTypes } from '@domain/model/enums/document-type/cmp-link';
import { DocumentType } from '@domain/model/enums/document-type/document-type.enum';
import { BARoutes } from '@utils/constants/routes/cmp-link';
import { AssetLink } from '@utils/helpers/asset-cpm-link';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RouterService } from '../core/router/router.service';
import { ConfigContextService } from '../services/config-context/config-context.service';
import { AccountCreatedEvent } from '../utils/interfaces/account-created-event.interface';
import { IBusinessAccountsConfig } from '../utils/interfaces/business-accounts-wc.config';
import { LegalPersonContainerComponent } from './legal-person-container/legal-person-container.component';
import { NaturalPersonContainerComponent } from './natural-person-container/natural-person-container.component';
import { DEFAULT_VIEW_CONFIG, ViewConfig } from './utils/types/view-config.type';
import { TraceService } from '@modules/business-accounts/services/trace/trace.service';
import { TraceError } from '@modules/business-accounts/utils/enums/trace-erros.enum';

@Component({
  selector: 'ba-container',
  templateUrl: './container.component.html',
  styleUrls: ['./container.component.scss'],
})
export class ContainerComponent extends BARoutes(DocumentTypes(AssetLink())) implements OnInit {
  config$!: Observable<IBusinessAccountsConfig | null>;

  isFlowActive: boolean = false;
  isBlacklisted: boolean = false;
  userHasMaxAccountsNumber: boolean = false;
  isInlineConfigActive: boolean = false;
  viewConfig: ViewConfig = DEFAULT_VIEW_CONFIG;

  @Output() processCompleted = new EventEmitter<{ id: string; status: string }>();
  @Output() accountCreated = new EventEmitter<AccountCreatedEvent[]>();

  @ViewChild(NaturalPersonContainerComponent) naturalPersonContainer!: NaturalPersonContainerComponent;
  @ViewChild(LegalPersonContainerComponent) legalPersonContainer!: LegalPersonContainerComponent;

  constructor(
    configContext: ConfigContextService,
    private readonly cdr: ChangeDetectorRef,
    private readonly viewRouter: RouterService,
    private readonly sessionService: SessionService,
    private readonly traceService: TraceService
  ) {
    super();
    this.config$ = configContext.config$.pipe(tap((config) => (this.isInlineConfigActive = !!config?.inline)));
    this.config$.subscribe((obj: IBusinessAccountsConfig | null) => {
      this.sessionService.setCommonInfo = { documentNumber: obj?.documentType == DocumentType.DNI ? obj!.documentNumber : obj!.legalRepresentant };
    });
  }

  ngOnInit(): void {
    this.viewRouter.navigate(this.baRoutes.STARTUP);
  }

  toggleFlowVisibility(isVisible: boolean, documentType?: DocumentType) {
    if (this.isBlacklisted || this.userHasMaxAccountsNumber) {
      this.showUserUnableToContinue();
      if (documentType === DocumentType.DNI) {
        this.naturalPersonContainer.onSuccessStepDone();
      } else if (documentType === DocumentType.RUC) {
        this.legalPersonContainer.onSuccessStepDone();
      }
      return;
    }
    let showViewDataCollection: boolean = false;
    if (isVisible) {
      this.config$.subscribe((obj: IBusinessAccountsConfig | null) => {
        showViewDataCollection = obj?.documentType != null && (obj.documentNumber == '' || obj.companyDocumentNumber == '' || obj.legalRepresentant == '') && isVisible;
        if (showViewDataCollection)
          this.viewRouter.navigate(this.baRoutes.DATA_COLLECTION);
      });
    }
    this.isFlowActive = isVisible;

    if (isVisible || showViewDataCollection) return;

    if (documentType === DocumentType.DNI) {
      this.naturalPersonContainer.onSuccessStepDone();
    } else if (documentType === DocumentType.RUC) {
      this.legalPersonContainer.onSuccessStepDone();
    }
  }

  onViewConfigChange(config: ViewConfig) {
    this.viewConfig = {
      ...this.viewConfig,
      ...config,
    };

    if (this.isInlineConfigActive) {
      this.viewConfig = {
        ...this.viewConfig,
        showCloseButton: false,
      };
    }

    this.cdr.detectChanges();
  }

  onBackButton(isEnabled?: boolean) {
    if (!isEnabled) return;
    this.viewRouter.back();
  }

  showUserUnableToContinue() {
    alert(
      `De acuerdo a nuestras políticas internas, no podemos atender tu solicitud. Para mayor información puedes comunicarte a nuestra Banca Telefónica.`
    );
  }

  onUserMaxAccountsNumber() {
    this.traceService.registerError(TraceError.USER_MAX_ACCOUNTS_ERROR);
    this.showUserUnableToContinue();
    this.userHasMaxAccountsNumber = true;
    this.isFlowActive = false;
  }

  onUserBlacklist() {
    this.traceService.registerError(TraceError.USER_BLACKLIST_ERROR);
    this.showUserUnableToContinue();
    this.isBlacklisted = true;
    this.isFlowActive = false;
  }

  onFlowCompleted() {
    this.isFlowActive = false;
    this.isBlacklisted = false;
    this.userHasMaxAccountsNumber = false;
    this.isInlineConfigActive = false;
  }

  getDocumentNumber(documeNumber: string) {
    let docuNumber = documeNumber;
    const { documentNumber, personType, ruc } = this.sessionService.getCommonInfoValue;
    if (personType != undefined) {
      if (personType === 'PN')
        docuNumber = documentNumber;
      else
        docuNumber = ruc;
    }
    return docuNumber;
  }

  getRuc(rucNum: string) {
    let rucNumber = rucNum;
    const { ruc, personType } = this.sessionService.getCommonInfoValue;
    if (personType != undefined) {
      rucNumber = ruc;
    }
    return rucNumber;
  }

  validateContaineToShow(documentType: DocumentType, documentTypeValidator: DocumentType) {
    const { personType } = this.sessionService.getCommonInfoValue;
    let validate = false;
    if (personType == undefined)
      validate = documentType === documentTypeValidator;
    else
      validate = this.getIdByPersonType(personType) === documentTypeValidator;
    return validate;
  }

  getIdByPersonType(personType: string) {
    let id: DocumentType;
    if (personType === 'PN') id = DocumentType.DNI;
    else id = DocumentType.RUC;
    return id;
  }

  getLegalRepresentantNumber(legalRepresentant: string) {
    let legalRepresent = legalRepresentant;
    const { documentNumber, personType } = this.sessionService.getCommonInfoValue;
    if (personType != undefined) {
      legalRepresent = documentNumber;
    }
    return legalRepresent;
  }

  getCompanyDocumentNumber(companyDocumentNumber: string) {
    let companyDocNumber = companyDocumentNumber;
    const { ruc, personType } = this.sessionService.getCommonInfoValue;
    if (personType != undefined) {
      companyDocNumber = ruc;
    }
    return companyDocNumber;
  }

  exitFlowCreateAccount() {
    const { personType } = this.sessionService.getCommonInfoValue;
    const document = this.getIdByPersonType(personType);
    this.toggleFlowVisibility(false, document);
  }
}
